// HT-4.5 完了パス · 実書類での確認状況（取り込み → 読み取り → 期待値 → 確認済み）
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, UploadSimple, Prohibit, ArrowUUpLeft } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { LEARN, API, authHeader, detailOf, Panel, Pill, Collapse } from './shared';
import { folderOptions } from '@/pages/docs/folderTree';

const Intake = ({ row, folders, busy, onDone }) => {
  const fileRef = useRef(null);
  const [folderId, setFolderId] = useState('');
  const [file, setFile] = useState(null);
  const [running, setRunning] = useState(false);
  const [note, setNote] = useState('');

  const run = async () => {
    if (!file || !folderId) return;
    setRunning(true);
    setNote('');
    let uploadedTitle = '';
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', file.name.replace(/\.[^.]+$/, ''));
      fd.append('folder_id', folderId);
      fd.append('doc_type', 'drawing');
      fd.append('confidentiality', 'internal');
      const up = await axios.post(`${API}/api/dms/documents/upload`, fd,
        { headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' } });
      uploadedTitle = up.data.title || file.name;
      const attached = await axios.post(
        `${LEARN}/coverage/${row.profile_key}/attach`,
        { document_id: up.data.id }, { headers: authHeader() });
      toast.success(`${row.label_ja}の実書類を登録しました`);
      setNote(`${attached.data.next_step_ja} ${attached.data.prefilled_fields_ja}`);
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      onDone?.();
    } catch (e) {
      const msg = detailOf(e, '取り込めませんでした');
      setNote(uploadedTitle
        ? `${msg} 別の書類を選んでください。取り込んだ「${uploadedTitle}」は書類一覧に残っています。`
        : msg);
      toast.error(msg);
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } finally { setRunning(false); }
  };

  return (
    <div className="rounded-md border border-dashed border-zinc-300 bg-zinc-50/60 px-3 py-2"
      data-testid={`coverage-intake-${row.profile_key}`}>
      <p className="text-[11.5px] font-medium text-zinc-800">実書類を取り込む</p>
      <p className="pb-1.5 text-[11px] text-zinc-500">
        1. 実書類を選ぶ → 2. 自動で読み取り → 3. 下の「検証セット」で期待値を入力 → 4. 実書類で確認済み
      </p>
      <div className="flex flex-wrap items-center gap-1.5">
        <input ref={fileRef} type="file" className="hidden"
          id={`coverage-file-${row.profile_key}`}
          accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff,.webp"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid={`coverage-file-${row.profile_key}`} />
        <label htmlFor={`coverage-file-${row.profile_key}`}
          className="inline-flex min-h-[40px] cursor-pointer items-center rounded-md border border-zinc-200 bg-white px-3 text-[11.5px] text-zinc-800 transition-colors duration-200 hover:bg-zinc-50"
          data-testid={`coverage-file-label-${row.profile_key}`}>
          ファイルをえらぶ
        </label>
        <span className="max-w-[220px] truncate text-[11.5px] text-zinc-600"
          title={file?.name || ''} data-testid={`coverage-file-name-${row.profile_key}`}>
          {file?.name || 'ファイルは選ばれていません'}
        </span>
        <select value={folderId} onChange={(e) => setFolderId(e.target.value)}
          aria-label="保存するフォルダーをえらぶ"
          className="h-10 rounded-md border border-zinc-200 px-2 text-[11.5px]"
          data-testid={`coverage-folder-${row.profile_key}`}>
          <option value="">フォルダーをえらぶ</option>
          {folderOptions(folders).map((o) => (
            <option key={o.value} value={o.value} title={o.pathLabel}>{o.label}</option>
          ))}
        </select>
        <Button size="sm" className="h-10 bg-teal-700 text-[12px] hover:bg-teal-800"
          disabled={busy || running || !file || !folderId} onClick={run}
          data-testid={`coverage-intake-run-${row.profile_key}`}>
          <UploadSimple size={12} className="mr-1" />
          {running ? '取り込み中…' : '取り込んで読み取る'}
        </Button>
      </div>
      {note && (
        <p className="pt-1.5 text-[11.5px] text-zinc-700"
          data-testid={`coverage-intake-note-${row.profile_key}`}>{note}</p>
      )}
    </div>
  );
};

const ScopeControl = ({ row, busy, onSubmit }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  if (row.excluded) {
    return (
      <Button size="sm" variant="outline" className="h-9 text-[11.5px]" disabled={busy}
        onClick={() => onSubmit(row, false, '')}
        data-testid={`coverage-include-${row.profile_key}`}>
        <ArrowUUpLeft size={12} className="mr-1" />対象に戻す
      </Button>
    );
  }
  if (!open) {
    return (
      <Button size="sm" variant="outline" className="h-9 text-[11.5px]" disabled={busy}
        onClick={() => setOpen(true)}
        data-testid={`coverage-exclude-${row.profile_key}`}>
        <Prohibit size={12} className="mr-1" />対象外にする
      </Button>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <input value={reason} onChange={(e) => setReason(e.target.value)}
        placeholder="対象外にする理由（例: 自社では作成しない）"
        aria-label={`${row.label_ja}を対象外にする理由`}
        className="h-9 w-64 rounded-md border border-zinc-200 px-2 text-[11.5px]"
        data-testid={`coverage-exclude-reason-${row.profile_key}`} />
      <Button size="sm" className="h-9 bg-zinc-800 text-[11.5px] hover:bg-zinc-900"
        disabled={busy || !reason.trim()}
        onClick={() => { onSubmit(row, true, reason.trim()); setOpen(false); setReason(''); }}
        data-testid={`coverage-exclude-save-${row.profile_key}`}>記録して対象外にする</Button>
      <Button size="sm" variant="outline" className="h-9 text-[11.5px]"
        onClick={() => setOpen(false)}
        data-testid={`coverage-exclude-cancel-${row.profile_key}`}>やめる</Button>
    </div>
  );
};

export default function CoverageGaps({ onChanged }) {
  const [d, setD] = useState(null);
  const [folders, setFolders] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, f] = await Promise.all([
        axios.get(`${LEARN}/coverage-gaps`, { headers: authHeader() }),
        axios.get(`${API}/api/dms/folders`, { headers: authHeader() }),
      ]);
      setD(c.data);
      setFolders((f.data || []).filter((x) => !x.is_archive_folder));
    } catch (e) { toast.error(detailOf(e, '確認状況を取得できませんでした')); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      const out = await fn();
      toast.success(out?.message_ja || ok);
      await load();
      onChanged?.();
    } catch (e) { toast.error(detailOf(e, '処理できませんでした')); } finally { setBusy(false); }
  };

  const addDoc = (row, documentId) => run(async () => {
    await axios.post(`${LEARN}/coverage/${row.profile_key}/attach`,
      { document_id: documentId }, { headers: authHeader() });
  }, '検証用の書類に追加しました');

  const setScope = (row, excluded, reason) => run(async () => {
    const r = await axios.post(`${LEARN}/coverage/${row.profile_key}/scope`,
      { excluded, reason }, { headers: authHeader() });
    return r.data;
  }, '対象の設定を変えました');

  if (!d) return null;
  return (
    <Panel title="実書類での確認状況" testId="learn-coverage"
      hint="実書類で確認できていない種類は、その種類だけ有効化できません。ここから実書類を取り込んで確認を完了できます。">
      <p className="flex flex-wrap gap-1.5" data-testid="coverage-progress">
        <Pill tone={d.open_gaps === 0 ? 'teal' : 'amber'}>{d.progress_ja}</Pill>
        <Pill tone={d.open_gaps === 0 ? 'teal' : 'zinc'}>
          {d.open_gaps === 0 ? '未対応の種類はありません' : `未対応 ${d.open_gaps}種類`}
        </Pill>
      </p>
      <div className="space-y-1.5 pt-2">
        {(d.rows || []).map((c) => (
          <div key={c.profile_key} className="rounded-md border border-zinc-200 px-3 py-2"
            data-testid={`coverage-${c.profile_key}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="flex flex-wrap items-center gap-1.5">
                <span className="text-[12.5px] font-medium text-zinc-900">{c.label_ja}</span>
                <Pill tone={c.real_document_validated ? 'teal' : c.excluded ? 'zinc' : 'amber'}
                  testId={`coverage-status-${c.profile_key}`}>{c.status_label_ja}</Pill>
                <Pill>実書類 {c.real_document_count}件</Pill>
                <Pill>期待値あり {c.documents_with_expected}件</Pill>
              </p>
              <div className="flex flex-wrap items-center gap-1.5">
                {c.target_set && (
                  <span className="text-[11px] text-zinc-500">
                    検証セット: {c.target_set.name}
                  </span>
                )}
                {!c.real_document_validated && (
                  <ScopeControl row={c} busy={busy} onSubmit={setScope} />
                )}
              </div>
            </div>
            <ul className="list-disc pl-5 pt-1 text-[11.5px] text-zinc-700"
              data-testid={`coverage-steps-${c.profile_key}`}>
              {(c.next_steps_ja || []).map((s) => <li key={s}>{s}</li>)}
            </ul>
            {c.excluded && (
              <p className="pt-0.5 text-[11px] text-zinc-500"
                data-testid={`coverage-excluded-note-${c.profile_key}`}>
                対象外にした人: {c.excluded_by || '—'}
                {c.excluded_at && ` · ${String(c.excluded_at).slice(0, 10)}`}
                {' '}· 対象外にしても、実書類がないためこの種類の学習内容は有効化されません
              </p>
            )}
            {c.blocked_reason_ja && !c.excluded && (
              <p className="pt-0.5 text-[11px] text-amber-900">{c.blocked_reason_ja}</p>
            )}
            {!c.real_document_validated && !c.excluded && (
              <div className="pt-1.5">
                <Intake row={c} folders={folders} busy={busy} onDone={load} />
              </div>
            )}
            {c.candidate_count > 0 && (
              <div className="pt-1.5">
                <Collapse label={`すでにある実書類から追加（${c.candidate_count}件）`}
                  testId={`coverage-candidates-${c.profile_key}`}>
                  <div className="space-y-1">
                    {(c.candidate_documents || []).map((x) => (
                      <div key={x.document_id}
                        className="flex flex-wrap items-center justify-between gap-2"
                        data-testid={`coverage-candidate-${x.document_id}`}>
                        <span className="text-[11.5px] text-zinc-700">
                          {x.drawing_number || x.title}
                          <span className="pl-1.5 text-zinc-400">{x.title}</span>
                        </span>
                        <Button size="sm" variant="outline" className="h-9 text-[11.5px]"
                          disabled={busy} onClick={() => addDoc(c, x.document_id)}
                          data-testid={`coverage-add-${x.document_id}`}>
                          <Plus size={12} className="mr-1" />検証セットに追加
                        </Button>
                      </div>
                    ))}
                  </div>
                </Collapse>
              </div>
            )}
          </div>
        ))}
      </div>
      <p className="pt-2 text-[11px] text-zinc-500">{d.notice_ja}</p>
    </Panel>
  );
}
